import axios from 'axios';
import { ethers } from 'ethers';
import { formatUnits } from 'ethers/lib/utils';

import { AssetPrices } from '~components/hooks/protocol/_types';
import { useAllChainlinkContracts } from '~components/hooks/protocol/useAllChainlinkContracts';
import {
  AddressString,
  TBalancerVault,
  TERC20,
  TGLPManager,
  TPriceFeed,
  TVSTOracle,
} from '~components/providers/types';
import { getAssetConfig, getAssetFromCoingeckoID, getContractAddress, getFarmConfig } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import {
  EVestaAsset,
  ALL_SINGLE_ASSET_VALUES,
  AllVestaAssets,
  ENativeAsset,
  EOtherAsset,
  VestaFarm,
} from '~config/types';
import { makeThrowable } from '~utils/error/errorHandlers';
import { ERROR_MSGS } from '~utils/error/errors';

export async function getOraclePrice<
  T extends {
    asset: EVestaAsset;
    priceFeedRead: TPriceFeed['read'];
    network: NetworkIds;
    returnRaw?: boolean;
  },
>({
  asset,
  priceFeedRead,
  network,
  returnRaw,
}: T): Promise<T['returnRaw'] extends boolean ? ethers.BigNumber : number> {
  const address = getContractAddress(asset, network);
  const priceRaw = await priceFeedRead.getExternalPrice([address]);
  const price = parseFloat(ethers.utils.formatEther(priceRaw));
  return returnRaw ? (priceRaw as any) : (price as any);
}

const getAllCoingeckoPrices = async () => {
  const coingeckoIds = [];
  const skipAssets = [EOtherAsset.STAKED_4_POOL, EOtherAsset.fsGLP];

  for (const asset of Object.values(ALL_SINGLE_ASSET_VALUES)) {
    if (!skipAssets.includes(asset as any)) {
      const assetConfig = getAssetConfig(asset)!;

      if (assetConfig.coingeckoId) {
        coingeckoIds.push(assetConfig.coingeckoId);
      }
    }
  }

  const coingeckoApiUrl = `https://api.coingecko.com/api/v3/simple/price?ids=${coingeckoIds
    .reduce((a, b) => a + ',' + b, '')
    .substring(1)}&vs_currencies=usd`;

  return await axios
    .get<Record<AllVestaAssets, number>>(coingeckoApiUrl, {
      transformResponse: data => {
        const jsonData = JSON.parse(data);
        return Object.keys(jsonData).reduce(
          (acc, cgId) => ({
            ...acc,
            [getAssetFromCoingeckoID(cgId)]: jsonData[cgId].usd,
          }),
          {},
        );
      },
    })
    .then(({ data }) => data);
};

const getAllOnChainPrices = async ({
  priceFeedRead,
  vstOracleRead,
  glpManagerRead,
  glpERC20Read,
  balancerVaultRead,
  network,
}: {
  priceFeedRead: TPriceFeed['read'];
  vstOracleRead: TVSTOracle['read'];
  glpManagerRead: TGLPManager['read'];
  glpERC20Read: TERC20['read'];
  balancerVaultRead: TBalancerVault['read'];
  network: NetworkIds;
}) => {
  const onChainCalls = [];

  Object.values(EVestaAsset).forEach(asset => {
    onChainCalls.push(
      (async () => {
        const price = await getOraclePrice({ asset, priceFeedRead, network });
        return { asset, price };
      })(),
    );
  });

  // VST price since coingecko is inaccurate
  onChainCalls.push(getOnChainVSTPrice(vstOracleRead));

  // GLP price
  onChainCalls.push(getMarketOnChainGLPPrice(glpManagerRead, glpERC20Read));

  // BAL pool
  onChainCalls.push(
    balancerVaultRead.getPoolTokens([getFarmConfig(VestaFarm['VSTA-ETH']).poolId as AddressString]),
  );

  const onChainCallsRes = await Promise.all(onChainCalls);
  const onChainPricesArray = onChainCallsRes.slice(0, -1) as {
    asset: EVestaAsset | ENativeAsset.VST;
    price: number;
    marketOverride?: boolean;
  }[];

  const balPool = onChainCallsRes[onChainCallsRes.length - 1] as [
    tokens: AddressString[],
    balances: bigint[],
    lastChangeBlock: bigint,
  ];

  const vestaAssetValues = Object.values(EVestaAsset);

  const onChainPrices = onChainPricesArray.reduce(
    (acc, { asset, price, marketOverride }) => {
      if (vestaAssetValues.includes(asset as EVestaAsset) && !marketOverride) {
        acc.oracle[asset as EVestaAsset] = price;
      } else {
        acc.marketOverrides[asset] = price;
      }
      return acc;
    },
    { oracle: {}, marketOverrides: {} } as any,
  ) as {
    oracle: Record<EVestaAsset, number>;
    marketOverrides: Record<Partial<AllVestaAssets>, number>;
  };

  onChainPrices.marketOverrides.VSTA =
    Number(balPool[1][0] / balPool[1][1]) * onChainPrices.oracle[EVestaAsset.ETH];

  return onChainPrices;
};

export const getOnChainVSTPrice = async (
  vstOracleRead: TVSTOracle['read'],
): Promise<{ asset: ENativeAsset.VST; price: number }> => {
  const rawPrice = await vstOracleRead.currentPrice();
  return { asset: ENativeAsset.VST, price: parseFloat(formatUnits(rawPrice, 8)) };
};

export const getMarketOnChainGLPPrice = async (
  glpManagerRead: TGLPManager['read'],
  glpERC20Read: TERC20['read'],
) => {
  const [aum, totalSupply] = await Promise.all([glpManagerRead.getAum([true]), glpERC20Read.totalSupply()]);
  const price = Number(aum / totalSupply / BigInt(10 ** 12));
  return {
    marketOverride: true,
    asset: EVestaAsset.sGLP,
    price,
  };
};

export const getChainlinkPrices = async (
  allChainlinkContracts: ReturnType<typeof useAllChainlinkContracts>,
): Promise<Record<AllVestaAssets, number>> => {
  return (
    await Promise.all(
      Object.entries(allChainlinkContracts).map(async ([asset, contract]) => ({
        [asset]: formatUnits((await contract.read.latestAnswer()).toString(), 8),
      })),
    )
  ).reduce((acc, curr) => ({ ...acc, ...curr }), {} as Record<AllVestaAssets, number>);
};

export const fallbackPrices = async ({
  allChainlinkContracts,
  balancerVaultRead,
  vestaOraclePrices,
}: {
  allChainlinkContracts: ReturnType<typeof useAllChainlinkContracts>;
  balancerVaultRead: TBalancerVault['read'];
  vestaOraclePrices: Record<EVestaAsset, number>;
}) => {
  const [chainlinkPrices, balPool] = await Promise.all([
    getChainlinkPrices(allChainlinkContracts),
    balancerVaultRead.getPoolTokens([getFarmConfig(VestaFarm['VSTA-ETH']).poolId as AddressString]),
  ]);

  return {
    ...chainlinkPrices,
    ...vestaOraclePrices,
    [ENativeAsset.VSTA]: Number(balPool[1][0] / balPool[1][1]) * vestaOraclePrices[EVestaAsset.ETH],
  };
};

export async function getAllPrices({
  network,
  priceFeedRead,
  vstOracleRead,
  glpManagerRead,
  glpERC20Read,
  balancerVaultRead,
  allChainlinkContracts,
}: {
  network: NetworkIds;
  priceFeedRead: TPriceFeed['read'];
  vstOracleRead: TVSTOracle['read'];
  glpManagerRead: TGLPManager['read'];
  glpERC20Read: TERC20['read'];
  balancerVaultRead: TBalancerVault['read'];
  allChainlinkContracts: ReturnType<typeof useAllChainlinkContracts>;
}): Promise<AssetPrices> {
  const { oracle, marketOverrides } = await getAllOnChainPrices({
    priceFeedRead,
    vstOracleRead,
    glpManagerRead,
    glpERC20Read,
    balancerVaultRead,
    network,
  });

  try {
    let marketPrices = {} as AssetPrices['market'];
    try {
      marketPrices = (await getAllCoingeckoPrices()) as any;
    } catch (err) {
      marketPrices = await fallbackPrices({
        allChainlinkContracts,
        balancerVaultRead,
        vestaOraclePrices: oracle,
      });
      console.error(err, 'err');
    }

    const finalData: AssetPrices = {
      oracle,
      market: { ...marketPrices, ...marketOverrides },
    };

    return finalData as AssetPrices;
  } catch (err) {
    console.warn(err);
    throw makeThrowable(ERROR_MSGS.CT_FETCH_SYS_PRICEFEED);
  }
}
